import { makeApi, Zodios, type ZodiosOptions } from '@zodios/core'
import { z } from 'zod'

const SyncPayloadUserInput = z
  .object({
    email: z.string(),
    name: z.string().nullish(),
    orgSlug: z.string().optional(),
    partnerId: z.string().nullish(),
    scopes: z.object({}).partial().loose().optional(),
  })
  .loose()
const SyncPayloadUserResponse = z
  .object({
    ok: z.literal(true),
    user: z.object({ id: z.number(), email: z.string() }).loose(),
  })
  .loose()
const ErrorResponse = z.object({ error: z.string() }).partial().loose()
const TenantSkuTestInfo = z
  .object({ name: z.string(), type: z.string(), validValues: z.array(z.string()) })
  .loose()
const TenantSkuOption = z
  .object({
    name: z.string(),
    value: z.string(),
    isNy: z.boolean(),
    isNonNy: z.boolean(),
    tests: z.array(TenantSkuTestInfo).optional(),
    rejectionReasons: z.array(z.string()).optional(),
  })
  .loose()
const TenantSkusResponse = z.object({ options: z.array(TenantSkuOption) }).loose()
const ThemeUpdatePayload = z
  .object({
    partnerId: z.string(),
    theme: z.object({}).partial().loose(),
    logo: z.number().int().nullish(),
  })
  .loose()
const ThemeUpdateResponse = z
  .object({ success: z.boolean(), error: z.string().optional(), message: z.string().optional() })
  .loose()
const DropShipmentOrderSkuRow = z.object({
  id: z.string().optional(),
  sku: z.string(),
  quantity: z.string(),
  additionalFields: z.record(z.string(), z.string()).optional(),
})
const DropShipmentOrderPayload = z.object({
  partnerId: z.string().nullable(),
  requesterName: z.string(),
  email: z.string(),
  shippingName: z.string(),
  shippingAddress1: z.string(),
  shippingAddress2: z.string().optional(),
  shippingCity: z.string(),
  shippingState: z.string(),
  shippingZip: z.string(),
  skuRows: z.array(DropShipmentOrderSkuRow).min(1),
  notes: z.string().optional(),
  duedate: z.string(),
})
const DropShipmentOrderResponse = z.object({ issueKey: z.string() })
const OrderSupportDeniedResponse = z.object({ resultType: z.literal('denied') }).loose()
const SupportTicket = z
  .object({
    id: z.number(),
    status: z.string(),
    subject: z.string(),
    kit_id: z.string(),
    type: z.string(),
    recipient: z.string(),
    tags: z.array(z.string()),
    custom_fields: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])),
    created_at: z.string(),
    updated_at: z.string(),
  })
  .loose()
const OrderSupportTicketsResponse = z
  .object({
    resultType: z.enum(['mocked', 'fetched']),
    count: z.number(),
    inaccessibleCount: z.number(),
    tickets: z.array(SupportTicket),
  })
  .loose()
const OrderSupportResponse = z.union([OrderSupportDeniedResponse, OrderSupportTicketsResponse])
const HipaaSessionInitRequest = z.object({ pending: z.boolean() }).partial().loose()
const HipaaSessionInitOkResponse = z.object({ ok: z.literal(true) }).loose()
const HipaaSessionInitErrorResponse = z.object({ ok: z.literal(false), reason: z.string() }).loose()
const HipaaSessionInitResponse = z.union([
  HipaaSessionInitOkResponse,
  HipaaSessionInitErrorResponse,
])

export const schemas = {
  SyncPayloadUserInput,
  SyncPayloadUserResponse,
  ErrorResponse,
  TenantSkuTestInfo,
  TenantSkuOption,
  TenantSkusResponse,
  ThemeUpdatePayload,
  ThemeUpdateResponse,
  DropShipmentOrderSkuRow,
  DropShipmentOrderPayload,
  DropShipmentOrderResponse,
  OrderSupportDeniedResponse,
  SupportTicket,
  OrderSupportTicketsResponse,
  OrderSupportResponse,
  HipaaSessionInitRequest,
  HipaaSessionInitOkResponse,
  HipaaSessionInitErrorResponse,
  HipaaSessionInitResponse,
}

const endpoints = makeApi([
  {
    method: 'post',
    path: '/api/client/drop-shipment-orders',
    alias: 'post_client_drop_shipment_orders',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DropShipmentOrderPayload,
      },
    ],
    response: z.object({ issueKey: z.string() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.object({ error: z.string() }).partial().loose(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.object({ error: z.string() }).partial().loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ error: z.string() }).partial().loose(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.object({ error: z.string() }).partial().loose(),
      },
    ],
  },
  {
    method: 'get',
    path: '/api/client/orders/:kitId/support',
    alias: 'get_client_orders_support',
    requestFormat: 'json',
    parameters: [
      {
        name: 'kitId',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'mock',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: OrderSupportResponse,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.object({ error: z.string() }).partial().loose(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.object({ error: z.string() }).partial().loose(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.object({ error: z.string() }).partial().loose(),
      },
    ],
  },
  {
    method: 'patch',
    path: '/api/client/settings/theme',
    alias: 'patch_client_settings_theme',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z
          .object({
            partnerId: z.string(),
            theme: z.object({}).partial().loose(),
            logo: z.number().int().nullish(),
          })
          .loose(),
      },
    ],
    response: z
      .object({
        success: z.boolean(),
        error: z.string().optional(),
        message: z.string().optional(),
      })
      .loose(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.object({ error: z.string() }).partial().loose(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.object({ error: z.string() }).partial().loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ error: z.string() }).partial().loose(),
      },
      {
        status: 404,
        description: `Tenant not found`,
        schema: z.object({ error: z.string() }).partial().loose(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.object({ error: z.string() }).partial().loose(),
      },
    ],
  },
  {
    method: 'post',
    path: '/api/hipaa-session-init',
    alias: 'post_hipaa_session_init',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ pending: z.boolean() }).partial().loose().optional(),
      },
    ],
    response: HipaaSessionInitResponse,
    errors: [
      {
        status: 401,
        description: `No Stytch session`,
        schema: HipaaSessionInitResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/payload/tenant-skus',
    alias: 'get_payload_tenant_skus',
    requestFormat: 'json',
    parameters: [
      {
        name: 'tenantId',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'includeTestOrderDetails',
        type: 'Query',
        schema: z.literal('true').optional(),
      },
    ],
    response: z.object({ options: z.array(TenantSkuOption) }).loose(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.object({ error: z.string() }).partial().loose(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.object({ error: z.string() }).partial().loose(),
      },
      {
        status: 403,
        description: `Forbidden`,
        schema: z.object({ error: z.string() }).partial().loose(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.object({ error: z.string() }).partial().loose(),
      },
    ],
  },
  {
    method: 'post',
    path: '/api/payload/users/sync',
    alias: 'post_payload_users_sync',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z
          .object({
            email: z.string(),
            name: z.string().nullish(),
            orgSlug: z.string().optional(),
            partnerId: z.string().nullish(),
            scopes: z.object({}).partial().loose().optional(),
          })
          .loose(),
      },
    ],
    response: z
      .object({
        ok: z.literal(true),
        user: z.object({ id: z.number(), email: z.string() }).loose(),
      })
      .loose(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.object({ error: z.string() }).partial().loose(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.object({ error: z.string() }).partial().loose(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.object({ error: z.string() }).partial().loose(),
      },
    ],
  },
])

// Schema type exports
export type SyncPayloadUserInputType = z.infer<typeof schemas.SyncPayloadUserInput>
export type SyncPayloadUserResponseType = z.infer<typeof schemas.SyncPayloadUserResponse>
export type ErrorResponseType = z.infer<typeof schemas.ErrorResponse>
export type TenantSkuTestInfoType = z.infer<typeof schemas.TenantSkuTestInfo>
export type TenantSkuOptionType = z.infer<typeof schemas.TenantSkuOption>
export type TenantSkusResponseType = z.infer<typeof schemas.TenantSkusResponse>
export type ThemeUpdatePayloadType = z.infer<typeof schemas.ThemeUpdatePayload>
export type ThemeUpdateResponseType = z.infer<typeof schemas.ThemeUpdateResponse>
export type DropShipmentOrderSkuRowType = z.infer<typeof schemas.DropShipmentOrderSkuRow>
export type DropShipmentOrderPayloadType = z.infer<typeof schemas.DropShipmentOrderPayload>
export type DropShipmentOrderResponseType = z.infer<typeof schemas.DropShipmentOrderResponse>
export type OrderSupportDeniedResponseType = z.infer<typeof schemas.OrderSupportDeniedResponse>
export type SupportTicketType = z.infer<typeof schemas.SupportTicket>
export type OrderSupportTicketsResponseType = z.infer<typeof schemas.OrderSupportTicketsResponse>
export type OrderSupportResponseType = z.infer<typeof schemas.OrderSupportResponse>
export type HipaaSessionInitRequestType = z.infer<typeof schemas.HipaaSessionInitRequest>
export type HipaaSessionInitOkResponseType = z.infer<typeof schemas.HipaaSessionInitOkResponse>
export type HipaaSessionInitErrorResponseType = z.infer<
  typeof schemas.HipaaSessionInitErrorResponse
>
export type HipaaSessionInitResponseType = z.infer<typeof schemas.HipaaSessionInitResponse>

export const api = new Zodios(endpoints)

export function createApiClient(baseUrl: string, options?: ZodiosOptions) {
  return new Zodios(baseUrl, endpoints, options)
}
